import { Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ScoutIdealProfile } from '@/modules/scout-sourcing/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';

type Props = {
  idealProfile: ScoutIdealProfile;
  onPreviewCandidates: () => void;
  onAbandon: () => void;
  disabled?: boolean;
  previewDisabled?: boolean;
  previewLoading?: boolean;
  previewButtonLabel?: string;
  previewStatusLabel?: string | null;
  previewActionHidden?: boolean;
  abandonLoading?: boolean;
  hasCandidatePreview?: boolean;
  talentPoolSummary?: ReactNode;
};

export function IdealProfileSummary({
  idealProfile,
  onPreviewCandidates,
  onAbandon,
  disabled,
  previewDisabled,
  previewLoading,
  previewButtonLabel,
  previewStatusLabel,
  previewActionHidden,
  abandonLoading,
  hasCandidatePreview,
  talentPoolSummary,
}: Props) {
  const hasTalentPoolSummary = Boolean(talentPoolSummary);
  const focusTags = [
    ...(idealProfile.mustHaves ?? []).slice(0, 3),
    idealProfile.targetGeography,
  ].filter(Boolean);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ideal candidate profile</CardTitle>
        <CardDescription>
          Scout translated your choices into a candidate profile and editable
          talent-pool filters.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!hasTalentPoolSummary && (
          <div className="bg-secondary-150 border-primary-600/25 rounded-lg border p-4 shadow-sm">
            <Subheading variant="h6" className="text-primary-700">
              Scout understands the brief
            </Subheading>
            <Paragraph className="text-primary-700/90 mt-1">
              {idealProfile.searchQueryRewrite || idealProfile.summary}
            </Paragraph>
            {focusTags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {focusTags.map((tag) => (
                  <Badge key={tag} variant="secondary" size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {previewStatusLabel && (
          <div className="border-border bg-muted/30 text-muted-foreground flex items-center gap-2 rounded-lg border px-4 py-3 text-sm">
            <Loader2Icon className="size-4 shrink-0 animate-spin" />
            <span>{previewStatusLabel}</span>
          </div>
        )}

        {talentPoolSummary && (
          <div className="border-primary-600/20 bg-secondary-150/80 rounded-lg border shadow-sm">
            {talentPoolSummary}
          </div>
        )}

        {idealProfile.mustHaves?.length > 0 && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-100/80 p-4">
            <Subheading variant="h6" className="text-neutral-700">
              Must-haves
            </Subheading>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {idealProfile.mustHaves.map((m) => (
                <Badge key={m}>{m}</Badge>
              ))}
            </div>
          </div>
        )}

        {idealProfile.niceToHaves?.length > 0 && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-100/80 p-4">
            <Subheading variant="h6" className="text-neutral-700">
              Nice-to-haves
            </Subheading>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {idealProfile.niceToHaves.map((m) => (
                <Badge key={m} variant="outline">
                  {m}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {idealProfile.acceptableTradeoffs?.length > 0 && (
          <div>
            <Subheading variant="h6" className="text-neutral-700">
              Acceptable tradeoffs
            </Subheading>
            <ul className="mt-1 list-disc pl-5 text-sm text-neutral-800">
              {idealProfile.acceptableTradeoffs.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {idealProfile.targetCompensation && (
            <div className="border-primary-600/20 bg-secondary-150/80 rounded-lg border p-4 shadow-sm">
              <Paragraph variant="caption" className="text-neutral-600">
                Target compensation
              </Paragraph>
              <Paragraph className="mt-1 text-neutral-800">
                {idealProfile.targetCompensation}
              </Paragraph>
            </div>
          )}
          {idealProfile.targetGeography && (
            <div className="border-primary-600/20 bg-secondary-150/80 rounded-lg border p-4 shadow-sm">
              <Paragraph variant="caption" className="text-neutral-600">
                Target geography
              </Paragraph>
              <Paragraph className="mt-1 text-neutral-800">
                {idealProfile.targetGeography}
              </Paragraph>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onAbandon}
            disabled={disabled}
            loading={abandonLoading}
          >
            Start over
          </Button>
          {!previewActionHidden && (
            <Button
              onClick={onPreviewCandidates}
              disabled={disabled || previewDisabled}
              loading={previewLoading}
            >
              {previewButtonLabel ??
                (hasCandidatePreview
                  ? 'Refresh preview'
                  : 'Preview candidates')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
